import { ListenableSubscribable } from '@benzinga/subscribable';
import { ConnatixLiveStreamResponse } from './entities';

export type LivestreamData = ConnatixLiveStreamResponse;

interface ConnatixLivestreamUpdate {
  data: LivestreamData | undefined;
  type: 'livestream:layouts_list_update';
}

export type LivestreamStoreEvent = ConnatixLivestreamUpdate;

const LOCAL_STORAGE_KEY = 'livestream';

export type ConnatixDataKey = 'connatix';
export type StreamDataKey = ConnatixDataKey;

export class LivestreamStore extends ListenableSubscribable<LivestreamStoreEvent> {
  private date?: Date;
  private streamDatas?: Map<StreamDataKey, LivestreamData>;

  constructor() {
    super();
    this.date = undefined;
    this.streamDatas = this.getInitialStreamData();
  }

  public shouldFetchStreamDataSettings = (key: StreamDataKey): boolean => {
    const ONE_MIN = 100 * 100000; /* ms */
    const lastCalled = this.date?.getTime() ?? 0;
    if (this.streamDatas === undefined || this.getLivestreamData(key) || Date.now() - lastCalled > ONE_MIN) {
      return true;
    }
    return false;
  };

  public getLivestreamData = (key: StreamDataKey): LivestreamData | undefined => {
    return this.streamDatas?.get(key);
  };

  public clearStreamDatas = () => {
    this.streamDatas?.clear();
    this.dispatch({
      data: undefined,
      type: 'livestream:layouts_list_update',
    });
    this.removeDataFromLocalStorage();
  };

  public setLivestreamData = (key: StreamDataKey, data: LivestreamData): void => {
    if (!this.streamDatas) {
      this.streamDatas = new Map();
    }

    this.streamDatas?.set(key, data);
    this.updateLocalStorageData();
  };

  public removeLivestreamData = (key: StreamDataKey): void => {
    this.streamDatas?.delete(key);
    this.updateLocalStorageData();
  };

  private updateLocalStorageData = () => {
    if (this.streamDatas) {
      window?.localStorage?.setItem(LOCAL_STORAGE_KEY, JSON.stringify([...this.streamDatas]));
    }
  };

  private getDataFromLocalStorage = (): Map<StreamDataKey, LivestreamData> | undefined => {
    const data = window?.localStorage?.getItem(LOCAL_STORAGE_KEY);
    return data ? JSON.parse(data) : undefined;
  };

  private removeDataFromLocalStorage = () => {
    window?.localStorage.removeItem(LOCAL_STORAGE_KEY);
  };

  private getInitialStreamData = () => {
    const localStorageData = this.getDataFromLocalStorage();

    return localStorageData ? new Map(localStorageData) : undefined;
  };
}
